import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { confirmAction } from '../../components/ConfirmDialog';
import { alumniAPI } from '../../services/api';
import { 
    FaArrowLeft, FaEye, FaCheckCircle, FaTimesCircle, FaGraduationCap,
    FaFilter, FaEnvelope, FaPhone, FaBriefcase, FaBuilding
} from 'react-icons/fa';
import {
    DashboardHeader,
    StatusBadge,
    LoadingSpinner,
    EmptyState
} from '../../components/dashboard';

const ReviewAlumni = () => {
    const [alumniApplications, setAlumniApplications] = useState([]);
    const [filter, setFilter] = useState('pending');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAlumniApplications();
        const interval = setInterval(loadAlumniApplications, 30000);
        return () => clearInterval(interval);
    }, [filter]);

    const loadAlumniApplications = async () => {
        try {
            const filters = filter !== 'all' ? { application_status: filter } : {};
            const res = await alumniAPI.getAll(filters);
            setAlumniApplications(res.applications || []);
        } catch (err) {
            console.error('Failed to load alumni applications:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id) => {
        const confirmed = await confirmAction('Approve this alumni application?');
        if (!confirmed) return;
        
        try {
            await alumniAPI.approve(id, 'Approved by student manager');
            toast.success('Alumni application approved!');
            loadAlumniApplications();
        } catch (err) {
            console.error('Failed to approve:', err);
            toast.error('Failed to approve application.');
        }
    };

    const handleReject = async (id) => {
        const confirmed = await confirmAction('Reject this alumni application?');
        if (!confirmed) return;
        
        const reason = 'Rejected by student manager';
        try {
            await alumniAPI.reject(id, reason);
            toast.success('Alumni application rejected.');
            loadAlumniApplications();
        } catch (err) {
            console.error('Failed to reject:', err);
            toast.error('Failed to reject application.');
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 py-8">
                <Link to="/dashboard" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors">
                    <FaArrowLeft className="text-sm" />
                    <span className="font-medium">Back to Dashboard</span>
                </Link>

                <DashboardHeader
                    icon={FaGraduationCap}
                    title="Review Alumni Applications"
                    subtitle="Review and manage alumni applications"
                    iconBgColor="bg-purple-50"
                    iconColor="text-purple-600"
                />

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center gap-3">
                        <FaFilter className="text-gray-500" />
                        <div className="flex gap-2 flex-wrap">
                            {['pending', 'approved', 'rejected', 'all'].map(status => (
                                <button
                                    key={status}
                                    onClick={() => setFilter(status)}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        filter === status 
                                            ? 'bg-purple-600 text-white shadow-sm' 
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {status.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    {alumniApplications.length === 0 ? (
                        <EmptyState
                            icon={FaGraduationCap}
                            message="No alumni applications found"
                        />
                    ) : (
                        alumniApplications.map((alumni) => (
                            <div key={alumni.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {alumni.first_name} {alumni.last_name}
                                            </h3>
                                            <StatusBadge status={alumni.application_status || 'pending'} />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <FaEnvelope className="text-gray-400" />
                                                <span>{alumni.email}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <FaPhone className="text-gray-400" />
                                                <span>{alumni.phone || 'N/A'}</span>
                                            </div>
                                            {alumni.current_position && (
                                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                                    <FaBriefcase className="text-gray-400" />
                                                    <span>{alumni.current_position}</span>
                                                </div>
                                            )}
                                        </div>
                                        {alumni.company_name && (
                                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                                                <FaBuilding className="text-gray-400" />
                                                <span>{alumni.company_name}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-2 ml-4">
                                        <Link
                                            to={`/alumni/${alumni.id}/review`}
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="View Details"
                                        >
                                            <FaEye />
                                        </Link>
                                        {alumni.application_status === 'pending' && (
                                            <>
                                                <button
                                                    onClick={() => handleApprove(alumni.id)}
                                                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                    title="Approve"
                                                >
                                                    <FaCheckCircle />
                                                </button>
                                                <button
                                                    onClick={() => handleReject(alumni.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Reject"
                                                >
                                                    <FaTimesCircle />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReviewAlumni;
